import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, Popup } from 'semantic-ui-react'
import dayjs from 'dayjs'
import { WidgetCard } from '../../style'
import { DocumentList, DocumentItem, FileInfo, FileMeta, FileType } from './style'
import DairyService from '~/api/services/dairy'
import Dialog from '~/components/Dialog'
import { handleErrors } from '~/utils/helpers'
import { DashboardQuery } from '~/QueryHooks/DashboardQuery'
import { SegmentationTableWidgetSkeleton } from '../../Skeletons'
import { SegmentationFileType } from '~/api/services/dashboard'
import CustomIcon from '~/components/CustomIcon'

const SegmentationTableWidget: React.FC = () => {
  const { t } = useTranslation()
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  const {
    data: segmentationData,
    isLoading,
    isFetching,
    refetch: fetchData
  } = DashboardQuery.getDashboardSegmentationData()

  useEffect(() => {
    fetchData()
  }, [t])

  const data = segmentationData?.files as SegmentationFileType[] | undefined

  const handleFileClick = async (file: SegmentationFileType) => {
    setIsBusy(true)
    try {
      const response = await DairyService.getSegmentationFile(file.id)
      const base64 = response.data
      const isPdf = file.filename?.toLowerCase().endsWith('.pdf')

      if (isPdf) {
        setPreviewSrc(base64)
      } else {
        const link = document.createElement('a')
        link.href = base64
        link.download = file.filename || file.name || ''
        link.click()
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  if (isLoading || isFetching) return <SegmentationTableWidgetSkeleton />

  return (
    <WidgetCard>
      <DocumentList>
        {data?.map((file) => {
          return <DocumentItem key={file.id}>
            <div className='left'>
              <div className='icon'>
                <Icon name={file.filename?.toLowerCase().endsWith('.pdf') ? 'file pdf outline' : 'file text outline'} />
              </div>

              <FileInfo>
                <div className='title'>{file.title}</div>
                <FileMeta>
                  {dayjs(file.month).format('MMMM YYYY')}
                  <FileType>{file.filename?.split('.').pop()?.toUpperCase()}</FileType>
                </FileMeta>
              </FileInfo>
            </div>

            <Popup
              content={t('Download')}
              trigger={<CustomIcon
                onClick={() => handleFileClick(file)}
                basic
                loading={isBusy}
                name='cloud download'
                style={{ fontSize: '1.3em' }} />
              }
            />
          </DocumentItem>
        })}
      </DocumentList>

      <Dialog
        open={Boolean(previewSrc)}
        handleClose={() => setPreviewSrc(null)}
        textContent={<iframe width='100%' height='100%' src={previewSrc} />}
        textHeader={t('Preview')}
        textClose={t('Close')}
        type='preview'
        isLoading={isBusy}
      />
    </WidgetCard>
  )
}

export default SegmentationTableWidget